import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";

const BlogStatusBox2 = ({ blogId = null, initialData = {}, onSuccess, isEdit }) => {
  const [status, setStatus] = useState("Publish");
  const [visibility, setVisibility] = useState("Public");
  const [password, setPassword] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [showVisEdit, setShowVisEdit] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [tempStatus, setTempStatus] = useState("Draft");
  const [tempVisibility, setTempVisibility] = useState("Public");

  useEffect(() => {
    if (initialData?.status) {
      setStatus(initialData.status);
      setTempStatus(initialData.status);
    }

    if (initialData?.visibility) {
      setVisibility(initialData.visibility);
      setTempVisibility(initialData.visibility);
      setShowPasswordInput(initialData.visibility === "Password");
    }

    if (initialData?.password) setPassword(initialData.password);
  }, [initialData]);

  // STATUS OK
  const handleStatusOk = () => {
    setStatus(tempStatus);
    setShowEdit(false);
  };

  const handleStatusCancel = () => {
    setTempStatus(status);
    setShowEdit(false);
  };

  // VISIBILITY OK
  const handleVisibilitySelect = (value) => {
    setTempVisibility(value);
    setShowPasswordInput(value === "Password");
  };

  const handleVisibilityOk = () => {
    setVisibility(tempVisibility);
    if (tempVisibility !== "Password") setPassword("");
    setShowVisEdit(false);
  };

  const handleVisibilityCancel = () => {
    setTempVisibility(visibility);
    setShowPasswordInput(visibility === "Password");
    if (visibility !== "Password") setPassword("");
    setShowVisEdit(false);
  };

  const saveBlog = async (actionType) => {
    try {
      setLoading(true);

      let finalStatus = tempStatus;
      const finalPayload = {
        status: finalStatus,
        visibility,
        password: visibility === "Password" ? password : "",
        actionType
      };

      if (onSuccess) {
        await onSuccess(finalPayload);
      }

      // toast.success(
      //   actionType === "publish"
      //     ? isEdit
      //       ? "✅ Blog Updated!"
      //       : "✅ Blog Published!"
      //     : "💾 Saved as Draft"
      // );
    } catch (err) {
      toast.error("Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (actionType) => {
    if (actionType === "preview") {
      saveBlog(actionType);
      // window.open(`/admin/preview-blog/${blogId || "temp"}`, "_blank");
      return;
    }
    saveBlog(actionType);
  };

  return (
    <div className="p-3 border rounded-lg bg-white shadow-sm">
      <h3 className="text-[16px] font-semibold text-black/80 mb-2">
        Blog Status
      </h3>

      {/* Buttons */}
      <div className="flex items-center gap-3">
        <button
          disabled={loading}
          className="w-full px-3 py-1 rounded-lg border font-semibold text-gray-800 border-gray-400 hover:bg-gray-100"
          onClick={() => handleAction("preview")}
        >
          Preview
        </button>

        <button
          disabled={loading}
          className="w-full px-3 py-1 rounded-lg bg-yellow-500 text-black font-semibold hover:bg-yellow-600"
          onClick={() => handleAction("draft")}
        >
          {loading ? "Saving..." : "Save as Draft"}
        </button>
      </div>

      {/* STATUS */}
      <div className="text-sm mt-3">
        <p>
          Status: <span className="font-medium">{status}</span>{" "}
          <span
            className="text-blue-600 underline cursor-pointer"
            onClick={() => setShowEdit(true)}
          >
            edit
          </span>
        </p>

        {showEdit && (
          <div className="mt-2">
            <select
              value={tempStatus}
              onChange={(e) => setTempStatus(e.target.value)}
              className="w-full p-2 border rounded-md text-sm"
            >
              <option value="Publish">Publish</option>
              <option value="Draft">Draft</option>
            </select>

            <div className="flex justify-end gap-3 mt-2">
              <button
                className="bg-blue-600 text-white px-3 py-1 rounded text-xs"
                onClick={handleStatusOk}
              >
                OK
              </button>
              <span
                className="text-gray-500 text-xs cursor-pointer"
                onClick={handleStatusCancel}
              >
                Cancel
              </span>
            </div>
          </div>
        )}
      </div>

      {/* VISIBILITY */}
      <div className="text-sm mt-3 pb-3">
        <p>
          Visibility: <span className="font-medium">{visibility}</span>{" "}
          <span
            className="text-blue-600 underline cursor-pointer"
            onClick={() => setShowVisEdit(true)}
          >
            edit
          </span>
        </p>

        {showVisEdit && (
          <div className="mt-2 space-y-3">
            <select
              value={tempVisibility}
              onChange={(e) => handleVisibilitySelect(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="Public">Public</option>
              <option value="Private">Private</option>
              <option value="Password">Password</option>
            </select>

            {showPasswordInput && (
              <div>
                <label className="text-xs">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                className="bg-blue-600 text-white px-3 py-1 rounded text-xs"
                onClick={handleVisibilityOk}
              >
                OK
              </button>
              <span
                className="text-xs text-gray-500 cursor-pointer"
                onClick={handleVisibilityCancel}
              >
                Cancel
              </span>
            </div>
          </div>
        )}
      </div>

      {/* PUBLISH / UPDATE BUTTON */}
      <div className="pt-3 border-t flex justify-end">
        <button
          disabled={loading}
          className="px-5 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          onClick={() => handleAction("publish")}
        >
          {loading ? "Publishing..." : isEdit ? "Update" :status}
        </button>
      </div>
    </div>
  );
};

export default BlogStatusBox2;
